import { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { JobBucket, JobListOptions } from "@shared-core/data/adapter";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { formatCount } from "@shared-core/domain/formatters";
import {
  fetchCustomerSuggestions,
  fetchChargeableJobSuggestions,
  gatherSuggestions,
  type SearchSuggestion,
} from "@shared-core/domain/searchSuggestions";
import { JobCard } from "../app/components/domain/orders/JobCard";
import {
  DateRangePicker,
  type DateRangePreset,
} from "../app/components/patterns/DateRangePicker";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { SalesOpsToggle } from "../app/components/patterns/SalesOpsToggle";
import { SearchTypeahead } from "../app/components/patterns/SearchTypeahead";
import { StatCard } from "../app/components/patterns/StatCard";
import { Card } from "../app/components/primitives/card";
import { JobDetailModal } from "../app/components/domain/orders/JobDetailModal";
import { ShoppingCart, Filter, ArrowUpDown } from "lucide-react";
import { CustomerScopeChip } from "../app/components/domain/customers/CustomerScopeChip";
import type { JobViewItem } from "@shared-core/data/api-types";
import type { DateRange } from "react-day-picker";
import { subDays, format } from "date-fns";
import { WorkPortfolioPage } from "./WorkPortfolioPage";
import { currentTime } from "@shared-core/domain/current-time";

// JobsPage follows the canonical list-endpoint pattern (see
// docs/list-endpoints.md): the backend does filter/sort/paginate.
// Date range + production bucket + sort + page are component state; a single
// useEffect issues one getChargeableJobs request per change, whose response
// carries the page of items alongside the server-computed bucket tab counts
// (bucketCounts) and the active-bucket total/value.
//
// A search (`q`) is treated as a universal ID lookup (backlog #7): while
// searching, the date window and bucket restriction are dropped so a job
// ordered outside the visible range, or sitting in a bucket other than the
// active tab, still surfaces. See `isSearching` below.

type SortOption =
  | "date-desc"
  | "date-asc"
  | "value-high"
  | "value-low"
  | "contribution-high"
  | "contribution-low";

// Production-aware buckets — the four partition the job set with no overlap.
const BUCKETS: readonly JobBucket[] = [
  "wip",
  "awaiting_invoice",
  "invoiced",
  "closed_not_billed",
];
const BUCKET_LABEL: Record<JobBucket, string> = {
  wip: "WIP",
  awaiting_invoice: "Awaiting invoice",
  invoiced: "Invoiced",
  closed_not_billed: "Closed, not billed",
};
// One line under the tabs where a bucket's meaning isn't self-evident.
const BUCKET_HINT: Partial<Record<JobBucket, string>> = {
  awaiting_invoice:
    "A job sits here once something has been delivered, until accounts raise the invoice in Tharstern. One picks up new invoices within about an hour.",
  closed_not_billed:
    "Closed in Tharstern with nothing invoiced (spoilage, held and closed, or billed on another job), or built into our own stock and billed on each call-off. These will never be invoiced.",
};

// `?range=` seeds the date window, so a link can open the list unwindowed
// (e.g. the sales home's awaiting-invoice tile, whose count is all-time).
const RANGE_PRESETS: readonly DateRangePreset[] = ["7d", "30d", "90d", "all"];

function initialDateRange(preset: DateRangePreset): DateRange {
  const to = currentTime();
  if (preset === "all") return { from: new Date(2000, 0, 1), to };
  const days = preset === "7d" ? 7 : preset === "90d" ? 90 : 30;
  return { from: subDays(to, days), to };
}

const PAGE_SIZE = 25;

const SORT_OPTION_TO_PARAM: Record<SortOption, string> = {
  "date-desc": "-ordered_at",
  "date-asc": "ordered_at",
  "value-high": "-booked_value",
  "value-low": "booked_value",
  "contribution-high": "-contribution_amt",
  "contribution-low": "contribution_amt",
};

function toIsoDate(d: Date | undefined): string | undefined {
  return d ? format(d, "yyyy-MM-dd") : undefined;
}

type ViewMode = "sales" | "work";

/**
 * Temporary consolidation: "Jobs" surfaces two related slices of the
 * same underlying job table (see chargeable_work migration). For now we
 * keep both, gated by a `?view=` toggle in the same pattern as
 * Customers → Pulse/Strategy. Sub-views own their own URL params + data
 * loading; the parent only owns the toggle.
 */
export function JobsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view: ViewMode = searchParams.get("view") === "work" ? "work" : "sales";

  const setView = useCallback(
    (next: ViewMode) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === "work") params.set("view", "work");
          else params.delete("view");
          // Reset paging when switching lenses; other params are
          // lens-specific so leaving them is harmless.
          params.delete("page");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-2xl">Jobs</h1>
        <SalesOpsToggle
          current={view === "work" ? "ops" : "sales"}
          onSelect={(v) => setView(v === "ops" ? "work" : "sales")}
        />
      </div>
      {view === "work" ? <WorkPortfolioPage embedded /> : <JobsListView />}
    </div>
  );
}

function JobsListView() {
  const adapter = useDataAdapter();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const customerFilter = searchParams.get("customer") || undefined;
  const csrFilter = searchParams.get("csr") || undefined;
  const salesRepFilter = searchParams.get("rep") || undefined;
  const mine = searchParams.get("mine") === "1";
  const urlBucket = searchParams.get("bucket");
  const initialBucket: JobBucket = BUCKETS.includes(urlBucket as JobBucket)
    ? (urlBucket as JobBucket)
    : "wip";

  const [jobs, setJobs] = useState<JobViewItem[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [bucket, setBucket] = useState<JobBucket>(initialBucket);
  const [selectedJob, setSelectedJob] = useState<JobViewItem | null>(null);
  // Scoped-customer name for the chip, resolved independently of the current
  // bucket. The list-derived name is empty whenever the active bucket has no
  // jobs for the customer (common: a customer with no WIP jobs), so fall back
  // to this so the scope chip still names who you filtered to.
  const [scopedCustomerName, setScopedCustomerName] = useState<string | null>(
    null,
  );

  const [datePreset, setDatePreset] = useState<DateRangePreset>(() => {
    const urlRange = searchParams.get("range") as DateRangePreset | null;
    return urlRange && RANGE_PRESETS.includes(urlRange) ? urlRange : "30d";
  });
  const [dateRange, setDateRange] = useState<DateRange | undefined>(() =>
    initialDateRange(datePreset),
  );

  const [sortBy, setSortBy] = useState<SortOption>("date-desc");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // CSR/AM and sales rep options for the person filter dropdowns (backlog
  // #18: jobs completed by a CSR but not yet invoiced; and a manager picking
  // one rep's jobs) — fetched once, independent of the active bucket/filters.
  const [csrOptions, setCsrOptions] = useState<string[]>([]);
  const [salesRepOptions, setSalesRepOptions] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    adapter
      .getJobCsrOptions()
      .then((csrs) => {
        if (!cancelled) setCsrOptions(csrs);
      })
      .catch(() => {
        if (!cancelled) setCsrOptions([]);
      });
    adapter
      .getJobSalesRepOptions()
      .then((reps) => {
        if (!cancelled) setSalesRepOptions(reps);
      })
      .catch(() => {
        if (!cancelled) setSalesRepOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  const setPersonFilter = useCallback(
    (param: "csr" | "rep", value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(param, value);
          else next.delete(param);
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setMine = useCallback(
    (next: boolean) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next) params.set("mine", "1");
          else params.delete("mine");
          params.delete("page");
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!customerFilter) {
      setScopedCustomerName(null);
      return;
    }
    let cancelled = false;
    adapter
      .getCustomerById(customerFilter)
      .then((detail) => {
        if (!cancelled) setScopedCustomerName(detail?.customer?.name ?? null);
      })
      .catch(() => {
        if (!cancelled) setScopedCustomerName(null);
      });
    return () => {
      cancelled = true;
    };
  }, [customerFilter, adapter]);

  // Typeahead suggestions: customers + jobs. A customer scopes the list to
  // that customer (clearing any typed text); a job jumps to its detail page.
  // Typed text alone still drives the live substring filter.
  const fetchSuggestions = useCallback(
    (q: string) =>
      gatherSuggestions(
        [fetchCustomerSuggestions, fetchChargeableJobSuggestions],
        adapter,
        q,
      ),
    [adapter],
  );

  const selectSuggestion = useCallback(
    (s: SearchSuggestion) => {
      if (s.group === "Customers") {
        setSearchQuery("");
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set("customer", s.id);
            next.set("page", "1");
            return next;
          },
          { replace: true },
        );
        return;
      }
      navigate(s.href);
    },
    [navigate, setSearchParams],
  );

  const [counts, setCounts] = useState<Record<JobBucket, number>>({
    wip: 0,
    awaiting_invoice: 0,
    invoiced: 0,
    closed_not_billed: 0,
  });
  // Booked-value (price_agreed) sum for the active bucket.
  const [bucketValue, setBucketValue] = useState(0);

  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("page", String(p));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const dateRangeOptions: Pick<JobListOptions, "bookedAfter" | "bookedBefore"> =
    useMemo(
      () => ({
        bookedAfter: toIsoDate(dateRange?.from),
        bookedBefore: toIsoDate(dateRange?.to),
      }),
      [dateRange],
    );

  // A search query is a universal ID lookup (job/order/invoice number as
  // well as description/customer name) — it must be able to find a job
  // regardless of when it was ordered or which production bucket it is
  // in, so the date window and bucket restriction are dropped while
  // searching (see docs/features/retire-booked-orders backlog #7).
  const isSearching = debouncedSearch.trim().length > 0;

  // Reset to page 1 when bucket/sort/search/date/csr/rep/mine changes.
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bucket,
    sortBy,
    debouncedSearch,
    dateRangeOptions,
    csrFilter,
    salesRepFilter,
    mine,
  ]);

  useEffect(() => {
    let cancelled = false;
    async function loadJobs() {
      const options: JobListOptions = {
        sort: SORT_OPTION_TO_PARAM[sortBy],
        page,
        pageSize: PAGE_SIZE,
      };
      if (!isSearching) {
        Object.assign(options, dateRangeOptions);
        options.bucket = bucket;
      } else {
        options.q = debouncedSearch.trim();
      }
      if (customerFilter) options.customerId = customerFilter;
      if (csrFilter) options.csr = csrFilter;
      if (salesRepFilter) options.salesRep = salesRepFilter;
      if (mine) options.mine = true;

      try {
        // One request: the page of items plus the server-computed bucket
        // counts and active-bucket total/value, all from a single query
        // pass (see jobs_query.py) instead of one probe per tab.
        const resp = await adapter.getChargeableJobs(options);
        if (cancelled) return;
        setJobs(resp.items);
        setTotalPages(resp.totalPages);
        setCounts({
          wip: resp.bucketCounts?.wip ?? 0,
          awaiting_invoice: resp.bucketCounts?.awaiting_invoice ?? 0,
          invoiced: resp.bucketCounts?.invoiced ?? 0,
          closed_not_billed: resp.bucketCounts?.closed_not_billed ?? 0,
        });
        setBucketValue(resp.totalBookedValue ?? 0);
      } catch (error) {
        console.error("Failed to load jobs:", error);
      } finally {
        if (!cancelled) {
          setFirstLoad(false);
        }
      }
    }
    loadJobs();
    return () => {
      cancelled = true;
    };
  }, [
    adapter,
    dateRangeOptions,
    bucket,
    sortBy,
    debouncedSearch,
    isSearching,
    customerFilter,
    csrFilter,
    salesRepFilter,
    mine,
    page,
  ]);

  // Total = sum of the production buckets (they partition with no overlap).
  const totalJobs = BUCKETS.reduce((sum, b) => sum + counts[b], 0);

  if (firstLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading jobs...</div>
      </div>
    );
  }

  const customerName = customerFilter
    ? (jobs.find((j) => j.customer_id === customerFilter)?.customer?.name ??
      scopedCustomerName)
    : null;

  const handleDismissCustomerScope = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("customer");
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header — h1 is owned by the JobsPage tabs shell. The
          dynamic customer-name title still surfaces via the scope chip. */}
      <div className="space-y-4">
        {customerFilter && (
          <CustomerScopeChip
            customerName={customerName}
            onDismiss={handleDismissCustomerScope}
          />
        )}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <SearchTypeahead
            value={searchQuery}
            onValueChange={setSearchQuery}
            placeholder="Search by job, order or invoice number, customer or description..."
            ariaLabel="Search jobs"
            fetchSuggestions={fetchSuggestions}
            onSelectSuggestion={selectSuggestion}
            className="flex-1 max-w-md"
          />
          <DateRangePicker
            date={dateRange}
            onDateChange={setDateRange}
            preset={datePreset}
            onPresetChange={setDatePreset}
            className="w-full sm:w-[280px]"
          />
        </div>
        {isSearching && (
          <p className="text-sm text-muted-foreground" role="status">
            Searching all jobs — the date range and status tabs below are
            ignored while a search is active.
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Jobs" value={formatCount(totalJobs)} />
        <StatCard label="Invoiced" value={formatCount(counts.invoiced)} />
        <StatCard label="Work in progress" value={formatCount(counts.wip)} />
        <StatCard
          // The value aggregate is scoped to the active bucket, but a search
          // drops the bucket filter — so while searching this is the value of
          // everything matched. Label it for what it actually is.
          label={
            isSearching ? "Matched value" : `${BUCKET_LABEL[bucket]} value`
          }
          value={`£${bucketValue.toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
        />
      </div>

      {/* Filters and Sort */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <div
            className={`flex gap-2 flex-wrap ${isSearching ? "opacity-50" : ""}`}
          >
            {BUCKETS.map((tab) => (
              <button
                key={tab}
                onClick={() => setBucket(tab)}
                disabled={isSearching}
                title={
                  isSearching
                    ? "Status tabs don't apply while searching"
                    : undefined
                }
                className={`px-3 py-1 rounded text-sm ${
                  isSearching ? "cursor-not-allowed" : ""
                } ${
                  bucket === tab && !isSearching
                    ? "bg-[color:var(--color-primary)] text-white"
                    : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                {BUCKET_LABEL[tab]} ({formatCount(counts[tab])})
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setMine(!mine)}
            aria-pressed={mine}
            title="Jobs for your customers, plus jobs you booked in"
            className={`px-3 py-1 rounded text-sm border ${
              mine
                ? "bg-[color:var(--color-primary)] text-white border-transparent"
                : "border-border bg-card text-muted-foreground hover:text-foreground"
            }`}
          >
            Mine
          </button>
          {csrOptions.length > 0 && (
            <select
              value={csrFilter ?? ""}
              onChange={(e) => setPersonFilter("csr", e.target.value)}
              aria-label="Filter by CSR/AM"
              className="flex-1 sm:flex-initial px-3 py-1 rounded border border-border bg-card text-sm"
            >
              <option value="">All CSR/AMs</option>
              {csrOptions.map((csr) => (
                <option key={csr} value={csr}>
                  {csr}
                </option>
              ))}
            </select>
          )}
          {salesRepOptions.length > 0 && (
            <select
              value={salesRepFilter ?? ""}
              onChange={(e) => setPersonFilter("rep", e.target.value)}
              aria-label="Filter by sales rep"
              className="flex-1 sm:flex-initial px-3 py-1 rounded border border-border bg-card text-sm"
            >
              <option value="">All sales reps</option>
              {salesRepOptions.map((rep) => (
                <option key={rep} value={rep}>
                  {rep}
                </option>
              ))}
            </select>
          )}
          <ArrowUpDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-muted-foreground hidden sm:inline">
            Sort by:
          </span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="flex-1 sm:flex-initial px-3 py-1 rounded border border-border bg-card text-sm"
          >
            <option value="date-desc">Date (newest first)</option>
            <option value="date-asc">Date (oldest first)</option>
            <option value="value-high">Sales Value (highest)</option>
            <option value="value-low">Sales Value (lowest)</option>
            <option value="contribution-high">Contribution (highest)</option>
            <option value="contribution-low">Contribution (lowest)</option>
          </select>
        </div>
      </div>

      {!isSearching && BUCKET_HINT[bucket] && (
        <p className="text-sm text-muted-foreground">{BUCKET_HINT[bucket]}</p>
      )}

      {/* Jobs List */}
      {jobs.length === 0 ? (
        <Card className="p-12 text-center">
          <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="mb-2">No jobs found</h3>
          <p className="text-muted-foreground">
            {isSearching
              ? "No jobs match your search"
              : `No ${BUCKET_LABEL[bucket].toLowerCase()} jobs found`}
          </p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onClick={() => setSelectedJob(job)}
            />
          ))}
        </div>
      )}

      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </div>
  );
}
